<script setup lang="ts">
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~~/app/factories/EndpointFactory';
    import LeadEndpoint from '~~/app/crm/endpoints/LeadEndpoint';
    import Lead from '~~/app/crm/models/Lead';
    import LeadSchema from '~~/app/crm/schemas/LeadSchema';

    const endpoint = EndpointFactory.make(ModelType.LEADS) as LeadEndpoint;
    const { contextMenu, onAction, onPromise } = useModelContextMenu();
    const archiveButton = ref(null);
    const unarchiveButton = ref(null);
    const deleteButton = ref(null);

    const unarchive = async (model: Lead) => {
        const schema = new LeadSchema({
            attributes: { archivedAt: null },
            id: model.getId(),
        });
        await unarchiveButton.value.loadUntil(onPromise(endpoint.update(schema)));
    };

    const archive = async (model: Lead) => {
        const schema = new LeadSchema({
            attributes: { archivedAt: moment().toUtcDateTimeString() },
            id: model.getId(),
        });
        await archiveButton.value.loadUntil(onPromise(endpoint.update(schema)));
    };

    const destroy = async (model: Lead) => {
        await deleteButton.value.loadUntil(onPromise(endpoint.destroy(model.getId())));
    };
</script>

<template>
    <ModelContextMenu
        v-slot="slotProps"
        ref="contextMenu"
        :model-type="Lead"
        :menu-height="200"
    >
        <a
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="onAction(() => slotProps.model?.open())"
        >
            <i
                class="fa-eye"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.open') }}</span>
        </a>
        <a
            href="#"
            :class="$theme('contextmenu.link')"
            @click.prevent="onAction(() => slotProps.model?.edit())"
        >
            <i
                class="fa-pencil"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.edit') }}</span>
        </a>
        <LoadingButton
            v-if="!slotProps.model.archivedAt"
            ref="archiveButton"
            :class-name="$theme('contextmenu.link')"
            @click.prevent="archive(slotProps.model)"
        >
            <i
                class="fa-box-archive"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.archive') }}</span>
        </LoadingButton>
        <LoadingButton
            v-if="slotProps.model.archivedAt"
            ref="unarchiveButton"
            :class-name="$theme('contextmenu.link')"
            @click.prevent="unarchive(slotProps.model)"
        >
            <i
                class="fa-inbox"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.unarchive') }}</span>
        </LoadingButton>
        <LoadingButton
            ref="deleteButton"
            :class-name="$theme('contextmenu.link')"
            @click.prevent="destroy(slotProps.model)"
        >
            <i
                class="fa-trash"
                :class="$theme('contextmenu.icon')"
            ></i>
            <span>{{ $t('actions.delete') }}</span>
        </LoadingButton>
    </ModelContextMenu>
</template>
